import {
    AvailabilityConfig,
    BillingInfo,
    Credentials,
    EmailAddress,
    Features,
    Profile,
    ProfileId,
    PublicConfig,
    Schedule,
    Student,
    StudentId,
    StudentName,
    TeacherName,
} from '@studoku/shared';
import { defineStore } from 'pinia';
import * as v from 'valibot';

import { http, installHandshakeHandlers, installMutationGuard } from './http';

interface AppState {
    authenticated: boolean;
    snackbar: { text: string; show: boolean; timeout?: number | undefined };

    studentLimitDialogShown: boolean;
    config: PublicConfig;
    features: Features;
    profile: Profile;
    allStudents: Student[];
    schedules: Schedule[];
    billingInfo: BillingInfo | null;

    // Deploy-handshake state, set from response headers.
    maintenance: boolean;
    updateAvailable: boolean;
    reloadRequired: boolean;
}

// Module scope: a timer handle is not serializable state.
let maintenancePollTimer: ReturnType<typeof setInterval> | null = null;
const MAINTENANCE_POLL_INTERVAL_MS = 4000;

let featuresInflight: Promise<void> | null = null;

export const useAppStore = defineStore('app', {
    state: (): AppState => ({
        authenticated: false,
        snackbar: { text: '', show: false },
        studentLimitDialogShown: false,
        config: {
            baseUrl: '',
            emailFromAddress: '',
            secureCookie: false,
        },
        features: {},
        profile: {
            id: v.parse(ProfileId, ''),
            name: v.parse(TeacherName, 'none'),
            email: v.parse(EmailAddress, 'none@example.invalid'),
            currentScheduleId: null,
            subscriptionInfo: null,
            emailChange: null,
            credentials: {
                password: false,
                google: false,
            },
            availabilityConfig: {
                startTime: { hour: 13, minute: 0 },
                endTime: { hour: 18, minute: 0 },
                ifNeededThreshold: 'neutral',
                breakPreference: 'none',
                preferences: [
                    {
                        weekday: 0,
                        startTime: { hour: 13, minute: 0 },
                        endTime: { hour: 18, minute: 0 },
                        selection: 'unavailable',
                    },
                    {
                        weekday: 1,
                        startTime: { hour: 13, minute: 0 },
                        endTime: { hour: 18, minute: 0 },
                        selection: 'available',
                    },
                    {
                        weekday: 2,
                        startTime: { hour: 13, minute: 0 },
                        endTime: { hour: 18, minute: 0 },
                        selection: 'available',
                    },
                    {
                        weekday: 3,
                        startTime: { hour: 13, minute: 0 },
                        endTime: { hour: 18, minute: 0 },
                        selection: 'available',
                    },
                    {
                        weekday: 4,
                        startTime: { hour: 13, minute: 0 },
                        endTime: { hour: 18, minute: 0 },
                        selection: 'available',
                    },
                    {
                        weekday: 5,
                        startTime: { hour: 13, minute: 0 },
                        endTime: { hour: 18, minute: 0 },
                        selection: 'available',
                    },
                    {
                        weekday: 6,
                        startTime: { hour: 13, minute: 0 },
                        endTime: { hour: 18, minute: 0 },
                        selection: 'unavailable',
                    },
                ],
            },
        },
        allStudents: [],
        schedules: [],
        billingInfo: null,
        maintenance: false,
        updateAvailable: false,
        reloadRequired: false,
    }),
    getters: {
        // Block writes during a window OR once this bundle is incompatible.
        mutationsBlocked: (state) => state.maintenance || state.reloadRequired,
        hasFeature: (state) => (flagName: string) => {
            return Boolean(state.features[flagName as keyof Features]);
        },
        students: (state) => {
            return state.allStudents.filter(
                (student) => student.archived !== true,
            );
        },
        studentLimitInfo:
            (state) =>
            (): { count: number; limit: number; remaining: number } => {
                // Count current active students (non-archived)
                const count = state.allStudents.filter(
                    (student) => student.archived !== true,
                ).length;

                // Check if self-signup is enabled
                if (!state.features['self-signup' as keyof Features]) {
                    // If self-signup is not enabled, there's no limit
                    return { count, limit: Infinity, remaining: Infinity };
                }

                // Treat null studentLimit as 0 when self-signup is enabled
                const limit =
                    state.profile?.subscriptionInfo?.studentLimit ?? 0;

                // Calculate remaining slots
                const remaining = limit - count;

                return { count, limit, remaining };
            },
    },
    actions: {
        // Call once at app startup; idempotent, so repeat calls are safe.
        installHandshake(): void {
            installHandshakeHandlers({
                onMaintenance: () => this.enterMaintenance(),
                onVersionRequired: () => this.requireReload(),
                onVersionChange: () => {
                    this.updateAvailable = true;
                },
            });
            // A predicate, not a value, so it tracks live state.
            installMutationGuard(() => this.mutationsBlocked);
        },
        // Never reloads, so in-progress edits survive the window.
        enterMaintenance(): void {
            if (this.maintenance) return;
            this.maintenance = true;
            if (maintenancePollTimer === null) {
                maintenancePollTimer = setInterval(() => {
                    void this.pollMaintenance();
                }, MAINTENANCE_POLL_INTERVAL_MS);
            }
        },
        // An incompatible server is caught by this response's epoch.
        async pollMaintenance(): Promise<void> {
            const response = await http('GET /api/config');
            if (response.success) {
                if (response.body.version) {
                    this.config = response.body;
                }
                this.exitMaintenance();
            }
        },
        exitMaintenance(): void {
            this.maintenance = false;
            if (maintenancePollTimer !== null) {
                clearInterval(maintenancePollTimer);
                maintenancePollTimer = null;
            }
        },
        // Only flags; UpdateRequiredDialog decides when to reload.
        requireReload(): void {
            this.reloadRequired = true;
        },
        async fetchFeatures(): Promise<void> {
            if (featuresInflight) return featuresInflight;
            featuresInflight = (async () => {
                try {
                    const featuresResponse = await http('GET /api/features');
                    if (featuresResponse.success) {
                        this.features = featuresResponse.body;
                    }
                } finally {
                    featuresInflight = null;
                }
            })();
            return featuresInflight;
        },
        async getBillingInfo(): Promise<void> {
            const response = await http('GET /api/billing/info');
            if (response.success) {
                this.billingInfo = response.body;
            } else {
                this.snackbar = {
                    text: `Failed to load billing information: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
            }
        },
        async init(): Promise<void> {
            const configResponse = await http('GET /api/config');
            if (configResponse.success) {
                this.config = configResponse.body;
            }
            await this.fetchFeatures();
            const profileResponse = await http('GET /api/profile');
            if (profileResponse.success) {
                this.profile = profileResponse.body;
            }
            // Load all students (both active and archived)
            const studentsResponse = await http('GET /api/students', {});
            if (studentsResponse.success) {
                this.allStudents = studentsResponse.body;
                this.allStudents.sort((a, b) => a.name.localeCompare(b.name));
            }
            const schedulesResponse = await http('GET /api/schedules', {});
            if (schedulesResponse.success) {
                this.schedules = schedulesResponse.body;
                this.schedules.sort((a, b) => b.id.localeCompare(a.id));
            }
            if (
                !configResponse.success ||
                !profileResponse.success ||
                !studentsResponse.success ||
                !schedulesResponse.success
            ) {
                this.snackbar = {
                    text: 'Failed to initialize app',
                    show: true,
                    timeout: 5000,
                };
            }
        },
        async refreshProfile(): Promise<void> {
            const profileResponse = await http('GET /api/profile');
            if (profileResponse.success) {
                this.profile = profileResponse.body;
            }
        },
        async login(
            credentials: v.InferInput<typeof Credentials>,
        ): Promise<{ success: boolean; message: string }> {
            const response = await http('POST /api/auth/login', credentials);
            if (response.success) {
                this.authenticated = true;
                await this.init();
                return { success: true, message: 'Login successful' };
            } else {
                return { success: false, message: response.message };
            }
        },
        async signInWithGoogle(idToken: string): Promise<{
            success: boolean;
            message: string;
        }> {
            if (!this.hasFeature('self-signup')) {
                return {
                    success: false,
                    message: 'Self-signup is not enabled',
                };
            }

            const response = await http('POST /api/auth/google', {
                idToken,
            });
            if (
                response.success &&
                response.body.success &&
                response.body.session
            ) {
                this.authenticated = true;
                await this.init();
                return { success: true, message: 'Google sign-in successful' };
            } else {
                return {
                    success: false,
                    message: response.success
                        ? response.body.message || 'Google sign-in failed'
                        : response.message,
                };
            }
        },
        async checkAuth(): Promise<void> {
            const response = await http('GET /api/auth/session');
            if (response.success) {
                this.authenticated = true;
                await this.init();
            } else {
                this.authenticated = false;
            }
        },
        async logout(): Promise<void> {
            await http('DELETE /api/auth/logout');
            this.authenticated = false;
            // Reset state to defaults
            this.profile = {
                id: v.parse(ProfileId, ''),
                name: v.parse(TeacherName, 'none'),
                email: v.parse(EmailAddress, 'none@example.invalid'),
                currentScheduleId: null,
                subscriptionInfo: null,
                emailChange: null,
                credentials: {
                    password: false,
                    google: false,
                },
                availabilityConfig: {
                    startTime: { hour: 13, minute: 0 },
                    endTime: { hour: 18, minute: 0 },
                    ifNeededThreshold: 'neutral',
                    breakPreference: 'none',
                    preferences: [
                        {
                            weekday: 0,
                            startTime: { hour: 13, minute: 0 },
                            endTime: { hour: 18, minute: 0 },
                            selection: 'unavailable',
                        },
                        {
                            weekday: 1,
                            startTime: { hour: 13, minute: 0 },
                            endTime: { hour: 18, minute: 0 },
                            selection: 'available',
                        },
                        {
                            weekday: 2,
                            startTime: { hour: 13, minute: 0 },
                            endTime: { hour: 18, minute: 0 },
                            selection: 'available',
                        },
                        {
                            weekday: 3,
                            startTime: { hour: 13, minute: 0 },
                            endTime: { hour: 18, minute: 0 },
                            selection: 'available',
                        },
                        {
                            weekday: 4,
                            startTime: { hour: 13, minute: 0 },
                            endTime: { hour: 18, minute: 0 },
                            selection: 'available',
                        },
                        {
                            weekday: 5,
                            startTime: { hour: 13, minute: 0 },
                            endTime: { hour: 18, minute: 0 },
                            selection: 'available',
                        },
                        {
                            weekday: 6,
                            startTime: { hour: 13, minute: 0 },
                            endTime: { hour: 18, minute: 0 },
                            selection: 'unavailable',
                        },
                    ],
                },
            };
            this.allStudents = [];
            this.schedules = [];
            this.billingInfo = null;
        },
        async saveProfile(profile: Profile): Promise<void> {
            // Check if email is being changed
            const emailChanged = profile.email !== this.profile.email;

            if (emailChanged) {
                // Handle email change through auth route first
                const emailResponse = await http(
                    'POST /api/auth/email/change-request',
                    {
                        newEmail: profile.email,
                    },
                );

                if (!emailResponse.success) {
                    this.snackbar = {
                        text: `Failed to initiate email change: ${emailResponse.message}`,
                        show: true,
                        timeout: 5000,
                    };
                    return;
                }
            }

            // Update other profile fields (email field will be ignored by the API)
            const response = await http('PUT /api/profile', profile);
            if (response.success) {
                this.profile = response.body;
                if (emailChanged) {
                    this.snackbar = {
                        text: 'Profile saved. Please check your email to verify the email change.',
                        show: true,
                        timeout: 5000,
                    };
                } else {
                    this.snackbar = {
                        text: 'Profile saved',
                        show: true,
                        timeout: 3000,
                    };
                }
            } else {
                this.snackbar = {
                    text: `Failed to save profile: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
            }
        },
        async savePassword({
            currentPassword,
            newPassword,
        }: {
            currentPassword: string;
            newPassword: string;
        }): Promise<boolean> {
            const response = await http('PUT /api/auth/password/change', {
                currentPassword,
                newPassword,
            });
            if (response.success) {
                this.snackbar = {
                    text: 'Password saved',
                    show: true,
                    timeout: 3000,
                };
                return true;
            } else {
                this.snackbar = {
                    text: `Failed to save password: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
                return false;
            }
        },
        async requestPasswordReset(
            email: string,
        ): Promise<{ success: boolean; message: string }> {
            const response = await http(
                'POST /api/auth/password/reset-request',
                {
                    email,
                },
            );
            if (response.success) {
                return { success: true, message: response.body.message };
            } else {
                return { success: false, message: response.message };
            }
        },
        async requestSignupEmailVerification(
            email: string,
        ): Promise<{ success: boolean; message: string }> {
            const response = await http('POST /api/auth/signup/email-request', {
                email,
            });
            if (response.success) {
                return { success: true, message: response.body.message };
            } else {
                return { success: false, message: response.message };
            }
        },
        async validateSignupEmailToken(
            token: string,
        ): Promise<{ valid: boolean; email?: string }> {
            const response = await http('GET /api/auth/signup/email-validate', {
                token,
            });
            if (response.success) {
                return {
                    valid: response.body.valid,
                    email: response.body.email,
                };
            } else {
                return { valid: false };
            }
        },
        async completeAccountWithToken(
            token: string,
            name: string,
            password: string,
        ): Promise<{ success: boolean; message: string }> {
            const response = await http('POST /api/auth/signup/complete', {
                token,
                name,
                password,
            });
            if (response.success) {
                // Auto-login was successful
                this.authenticated = true;
                await this.init();
                return {
                    success: true,
                    message: 'Account created successfully!',
                };
            } else {
                return { success: false, message: response.message };
            }
        },
        async completePasswordReset(
            token: string,
            newPassword: string,
        ): Promise<{ success: boolean; message: string }> {
            const response = await http(
                'POST /api/auth/password/reset-complete',
                {
                    token,
                    newPassword,
                },
            );
            if (response.success) {
                return {
                    success: true,
                    message:
                        'Password has been reset successfully! You can now sign in with your new password.',
                };
            } else {
                return { success: false, message: response.message };
            }
        },
        async saveAvailabilityConfig(
            availabilityConfig: AvailabilityConfig,
        ): Promise<void> {
            await this.saveProfile({ ...this.profile, availabilityConfig });
            this.snackbar = {
                text: 'Saved',
                show: true,
                timeout: 3000,
            };
        },
        async getStudents(): Promise<void> {
            // Load all students (both active and archived)
            const response = await http('GET /api/students', {});
            if (response.success) {
                this.allStudents = response.body;
                this.allStudents.sort((a, b) => a.name.localeCompare(b.name));
            } else {
                this.snackbar = {
                    text: `Failed to load students: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
            }
        },
        async getArchivedStudents(): Promise<Student[]> {
            const response = await http('GET /api/students', {
                archived: 'true',
            });
            if (response.success) {
                const students = response.body;
                students.sort((a, b) => a.name.localeCompare(b.name));
                return students;
            } else {
                this.snackbar = {
                    text: `Failed to load archived students: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
                return [];
            }
        },
        async getArchivedSchedules(): Promise<Schedule[]> {
            const response = await http('GET /api/schedules', {
                archived: 'true',
            });
            if (response.success) {
                const schedules = response.body;
                schedules.sort((a, b) => a.name.localeCompare(b.name));
                return schedules;
            } else {
                this.snackbar = {
                    text: `Failed to load archived schedules: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
                return [];
            }
        },
        async getSchedules(): Promise<void> {
            const response = await http('GET /api/schedules', {});
            if (response.success) {
                this.schedules = response.body;
                this.schedules.sort((a, b) => b.id.localeCompare(a.id));
            } else {
                this.snackbar = {
                    text: `Failed to load schedules: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
            }
        },
        // Returns whether the write was actually committed. Callers must
        // propagate this: a dropped write (e.g. blocked during a maintenance
        // window) must not be reported to the user as a successful save.
        async saveStudent(
            student: v.InferInput<typeof Student>,
        ): Promise<boolean> {
            const response = await http(
                'PUT /api/students/:id',
                { id: v.parse(StudentId, student.id) },
                student,
            );
            if (response.success) {
                const validatedStudent = v.parse(Student, student);
                if (this.allStudents.find((s) => s.id === student.id)) {
                    this.allStudents = this.allStudents.map((s) =>
                        s.id === student.id ? validatedStudent : s,
                    );
                } else {
                    this.allStudents.push(validatedStudent);
                }
                this.allStudents.sort((a, b) => a.name.localeCompare(b.name));
                return true;
            }
            this.snackbar = {
                text: `Failed to save student: ${response.message}`,
                show: true,
                timeout: 5000,
            };
            return false;
        },
        // Returns whether the delete was actually committed, for the same
        // reason as saveStudent: a dropped write must not be presented as done.
        async deleteStudent(student: Student): Promise<boolean> {
            const response = await http('DELETE /api/students/:id', {
                id: student.id,
            });
            if (response.success) {
                this.allStudents = this.allStudents.filter(
                    (s) => s.id !== student.id,
                );
                this.snackbar = {
                    text: `Student deleted: ${student.name}`,
                    show: true,
                    timeout: 3000,
                };
                return true;
            }
            this.snackbar = {
                text: `Failed to delete student: ${response.message}`,
                show: true,
                timeout: 5000,
            };
            return false;
        },
        async sendPreferenceSurvey(
            student: Student,
            dueByDate: Date,
        ): Promise<void> {
            const response = await http(
                'POST /api/students/:studentId/survey-emails',
                {
                    studentId: student.id,
                },
                {
                    dueByDate,
                },
            );
            if (response.success) {
                this.snackbar = {
                    text: `Preference survey sent to ${student.name}`,
                    show: true,
                    timeout: 3000,
                };
                await this.getStudents();
            } else {
                let errorMessage = `Failed to send survey to ${student.name}`;
                if (response.errorType === 'http') {
                    if (response.status === 404) {
                        errorMessage = `Student ${student.name} not found`;
                    } else if (response.status === 500) {
                        errorMessage = `Failed to send survey email to ${student.name}`;
                    }
                }
                this.snackbar = {
                    text: errorMessage,
                    show: true,
                    timeout: 5000,
                };
            }
        },
        async sendPreferenceSurveysBulk(
            studentIds: StudentId[],
            dueByDate: Date,
        ): Promise<void> {
            const response = await http(
                'POST /api/students/survey-emails/bulk',
                {
                    studentIds,
                    dueByDate,
                },
            );

            if (response.success) {
                const body = response.body as {
                    success: boolean;
                    failures?: StudentName[];
                };
                if (body.success) {
                    this.snackbar = {
                        text: `Preference surveys sent to ${studentIds.length} students`,
                        show: true,
                        timeout: 3000,
                    };
                    await this.getStudents();
                } else if (body.failures && body.failures.length > 0) {
                    const failedStudents = body.failures.join(', ');
                    this.snackbar = {
                        text: `Some surveys failed to send to: ${failedStudents}`,
                        show: true,
                        timeout: 8000,
                    };
                }
            } else {
                let errorMessage = 'Failed to send preference surveys';
                if (response.errorType === 'http') {
                    if (response.status === 404) {
                        errorMessage =
                            'Profile not found - unable to send surveys';
                    } else if (response.status === 500) {
                        errorMessage =
                            'All emails failed to send due to authentication failure';
                    }
                }
                this.snackbar = {
                    text: errorMessage,
                    show: true,
                    timeout: 5000,
                };
            }
        },
        async generateSurveyLink(
            student: Student,
            dueByDate: Date,
        ): Promise<string | null> {
            const response = await http(
                'POST /api/students/:studentId/survey-links',
                {
                    studentId: student.id,
                },
                {
                    dueByDate,
                },
            );
            if (response.success) {
                const updatedStudent = await http('GET /api/students/:id', {
                    id: student.id,
                });
                if (updatedStudent.success) {
                    this.allStudents = this.allStudents.map((s) =>
                        s.id === student.id ? updatedStudent.body : s,
                    );
                    return response.body.link;
                } else {
                    this.snackbar = {
                        text: `Failed to get updated student: ${updatedStudent.message}`,
                        show: true,
                        timeout: 5000,
                    };
                    return null;
                }
            } else {
                this.snackbar = {
                    text: `Failed to generate survey link: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
                return null;
            }
        },
        async saveSchedule(schedule: Schedule): Promise<Schedule | null> {
            const response = await http(
                'PUT /api/schedules/:id',
                { id: schedule.id },
                schedule,
            );
            if (response.success) {
                schedule = response.body;
                const newSchedules = [
                    ...this.schedules.filter((s) => s.id !== schedule.id),
                    schedule,
                ];
                newSchedules.sort((a, b) => b.id.localeCompare(a.id));
                this.schedules = newSchedules;
                this.snackbar = {
                    text: 'Schedule saved',
                    show: true,
                    timeout: 3000,
                };
                return schedule;
            } else {
                this.snackbar = {
                    text: `Failed to save schedule: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
                return null;
            }
        },
        async setAsCurrentSchedule(schedule: Schedule): Promise<void> {
            await this.saveProfile({
                ...this.profile,
                currentScheduleId: schedule.id,
            });
            this.snackbar = {
                text: 'Schedule set as current',
                show: true,
                timeout: 3000,
            };
        },
        async deleteSchedule(schedule: Schedule): Promise<void> {
            const response = await http('DELETE /api/schedules/:id', {
                id: schedule.id,
            });
            if (response.success) {
                this.schedules = this.schedules.filter(
                    (s) => s.id !== schedule.id,
                );
                this.snackbar = {
                    text: 'Schedule deleted',
                    show: true,
                    timeout: 3000,
                };
            } else {
                this.snackbar = {
                    text: `Failed to delete schedule: ${response.message}`,
                    show: true,
                    timeout: 5000,
                };
            }
        },
        markStudentLimitDialogShown(): void {
            this.studentLimitDialogShown = true;
        },
    },
});

const ISO_8601_DATETIME_REGEX: RegExp =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

export function convertTypes<T extends Record<string, unknown>>(o: T): T {
    if (typeof o !== 'object' || o === null) return o;
    for (const key of Object.keys(o) as (keyof T)[]) {
        const value = o[key];
        // noinspection SuspiciousTypeOfGuard
        if (typeof value === 'string' && ISO_8601_DATETIME_REGEX.test(value)) {
            o[key] = new Date(value) as unknown as T[keyof T];
        } else if (typeof value === 'object') {
            o[key] = convertTypes(
                value as Record<string, unknown>,
            ) as T[keyof T];
        }
    }
    return o;
}
